import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { AuthEvents } from '@modules/AuthModule/types';
import {
  STORAGE_KEY_AUTH_REDIRECT_URL,
  STORAGE_KEY_AUTH_RESPONSE,
  STORAGE_KEY_REFRESHED_AT,
  STORAGE_KEY_SERVICE_ID,
  STORAGE_KEY_SERVICE_NAME,
  STORAGE_KEY_TASKS,
  STORAGE_PREFIX_APPAUTH,
} from '../../constants/storage';
import environment from '@lib/environment';
import AnnouncementsModule from '@modules/AnnouncementsModule';
import { authState } from '@modules/AuthModule/state';
import ServiceModule from '@modules/ServiceModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { phoneState } from '@modules/SoftPhoneModule/state';
import ToastModule from '@modules/ToastModule';
import { browserState } from './state';
import { strings } from './strings';
import { LocaleSource, MicrophoneAccessType } from './types';
import { getClearBrowserDataErrorMessage } from './lib/getClearBrowserDataErrorMessage';
import {
  detectLocaleFromGeolocation,
  resolveLocaleFromCoords,
} from './lib/detectSupportedLocale';

const MICROPHONE_ACCESS_ANNOUNCEMENT_KEY = 'microphone-access';
const MICROPHONE_CHANGE_DEBOUNCE_MS = 500;

export class BrowserModule extends AppModule {
  override events = [
    'network.request.failed',
    'network.status.online',
    'network.status.offline',
  ];

  private _microphoneChangeCleanupFn?: () => void;
  private _microphoneChangeTimeout?: NodeJS.Timeout;
  /**
   * The watch ID returned by `navigator.geolocation.watchPosition`.
   * `undefined` when no watch is active.  Used by {@link stopGeolocationWatch}
   * to cancel the watch and by {@link startGeolocationWatch} to guard against
   * starting duplicate watches.
   */
  private _geolocationWatchId?: number;

  override preload(): void {
    this.app.events.on(AuthEvents.SIGNIN, () => {
      // check for microphone accessibility
      this.requestMicrophoneAccess();

      /**
       * Registers handler to respond to microphone change events
       * provided the feature flag is enabled to gracefully handle microphone disconnect
       */
      const tryAddMicrophoneChangeHandler = () => {
        const hasGracefullyHandleMicrophoneDisconnect =
          FeatureFlagModule.isEnabled(
            FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect,
          );

        if (hasGracefullyHandleMicrophoneDisconnect) {
          this.registerMicrophoneChangeHandler();
        }
      };

      if (FeatureFlagModule.areFlagsReady) tryAddMicrophoneChangeHandler();
      else {
        this.app.events.on(
          'featureflags.initialized',
          tryAddMicrophoneChangeHandler,
        );
      }
    });
  }

  override load(): void {
    this.requestNotificationPermission();
    window.addEventListener('unload', this.handleUnload);
    this.registerNetworkStatusHandler();
    void this.checkGeolocationPermissionAndWatch();
  }

  private readonly registerNetworkStatusHandler = () => {
    const onOnline = () => {
      browserState.isOnline = true;
      this.app.logger.info('network status changed: online');
      this.app.events.emit('network.status.online', {
        timestamp: Date.now(),
        isOnline: true,
      });
    };

    const onOffline = () => {
      browserState.isOnline = false;
      this.app.logger.info('network status changed: offline');
      this.app.events.emit('network.status.offline', {
        timestamp: Date.now(),
        isOnline: false,
      });
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  };

  /**
   * Checks the current geolocation permission state without triggering a
   * browser prompt, then responds accordingly:
   *
   * - `granted`: immediately starts the position watch via
   *   {@link startGeolocationWatch}.
   * - `prompt` / `denied`: does nothing on startup, but registers a
   *   `change` listener so the watch starts automatically if the user later
   *   grants access (e.g. by clicking the "I'm in a supported region" button).
   *
   * Called once during {@link load}.  Silently no-ops if either
   * `navigator.permissions` or `navigator.geolocation` is unavailable
   * (e.g. in non-secure contexts or unsupported browsers).
   */
  private readonly checkGeolocationPermissionAndWatch =
    async (): Promise<void> => {
      if (!navigator.permissions || !navigator.geolocation) return;

      try {
        const permission = await navigator.permissions.query({
          name: 'geolocation',
        });

        if (permission.state === 'granted') {
          this.startGeolocationWatch();
        }

        permission.addEventListener('change', () => {
          if (permission.state === 'granted') {
            this.startGeolocationWatch();
          } else {
            this.stopGeolocationWatch();
          }
        });
      } catch {
        // Permissions API unavailable or geolocation descriptor not recognised
      }
    };

  /**
   * Starts a continuous `watchPosition` subscription that keeps
   * `browserState.localeState` in sync with the device's physical location.
   *
   * Idempotent: if a watch is already active ({@link _geolocationWatchId} is
   * set), the call is a no-op.  This allows both the automatic startup path
   * (via {@link checkGeolocationPermissionAndWatch}) and the manual path (via
   * {@link attemptGeolocationDetection}) to safely call this method without
   * risk of duplicate watches.
   *
   * **Loading state**: when starting a fresh watch and the current
   * `localeState.source` is not `Geolocation` (i.e. locale has not yet been
   * GPS-confirmed), `localeState.isLoading` is set to `true` so the UI can
   * show a spinner while waiting for the first position fix.  If the source is
   * already `Geolocation` — which happens when `attemptGeolocationDetection`
   * calls this method after its own one-shot fix — `isLoading` is left
   * untouched to avoid a spurious re-render.
   *
   * Geolocation is treated as authoritative: each position update overwrites
   * `localeState.locale` and sets `localeState.source` to `Geolocation`
   * regardless of the previous value — including a potential downgrade to
   * `Unsupported` if the device reports coordinates outside US/CA bounds.
   * The success callback also clears `isLoading`.
   *
   * Watch options:
   * - `enableHighAccuracy: false` — network-based positioning is sufficient;
   *   avoids draining the device battery with GPS hardware.
   * - `timeout: 10_000` — position updates that take longer than 10 s trigger
   *   the error callback, which calls {@link stopGeolocationWatch}.
   * - `maximumAge: 60_000` — cached positions up to 60 s old are accepted,
   *   reducing unnecessary hardware wake-ups.
   */
  private readonly startGeolocationWatch = (): void => {
    if (this._geolocationWatchId !== undefined) return;

    // Signal loading only when the locale hasn't been GPS-confirmed yet.
    // If source is already Geolocation (e.g. called from attemptGeolocationDetection
    // after its one-shot fix), isLoading is already false and we leave it alone.
    if (browserState.localeState.source !== LocaleSource.Geolocation) {
      browserState.localeState.isLoading = true;
    }

    this._geolocationWatchId = navigator.geolocation.watchPosition(
      ({ coords: { latitude, longitude } }) => {
        // Geolocation is the authoritative source — update locale regardless
        // of whether the result is supported or not.
        browserState.localeState.locale = resolveLocaleFromCoords(
          latitude,
          longitude,
        );
        browserState.localeState.source = LocaleSource.Geolocation;
        browserState.localeState.isLoading = false;
      },
      () => this.stopGeolocationWatch(),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  /**
   * Cancels the active `watchPosition` subscription and clears
   * {@link _geolocationWatchId}.
   *
   * Also sets `localeState.isLoading` to `false` so any pending loading
   * indicator is dismissed when the watch is torn down.
   *
   * Called in two scenarios:
   * 1. The watch error callback fires (e.g. position timeout or permission
   *    revoked mid-session).
   * 2. The Permissions API fires a `change` event and the new state is not
   *    `granted` (e.g. the user manually revokes geolocation in browser
   *    settings while the app is open).
   *
   * No-ops if no watch is currently active.
   */
  private readonly stopGeolocationWatch = (): void => {
    if (this._geolocationWatchId !== undefined) {
      navigator.geolocation.clearWatch(this._geolocationWatchId);
      this._geolocationWatchId = undefined;
      browserState.localeState.isLoading = false;
    }
  };

  public showBrowserNotification = (
    title: string,
    options?: NotificationOptions,
  ) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, options);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  private handleUnload = async (event: BeforeUnloadEvent) => {
    if (SoftPhoneModule.isInCall()) {
      this.app.logger.info('app unloaded while in call');
      // Recommended to display the browser confirmation dialog
      event.preventDefault();

      ToastModule.showToast({
        title: strings.toasts.closingWhileInCall,
        status: 'warning',
      });

      return;
    } else {
      SoftPhoneModule.disconnectSoftPhone();
    }

    // set the user offline if they have a softphone active and they are not offline
    const user = ServiceModule.getCurrentUser();
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureOfflineOnClose) &&
      user?.id &&
      phoneState.phoneConnected &&
      ServiceModule.getAvailabilityStatus(user) !==
        UserSetAvailabilityStatus.Offline
    ) {
      event.preventDefault();
      navigator.sendBeacon(
        environment.API_URL + '/v1/tab-close',
        authState.token,
      );
      ToastModule.showToast({
        title: strings.toasts.setUserOfflineToast,
        status: 'warning',
      });
    }
  };

  private requestNotificationPermission = async () => {
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        this.app.logger.info('notification permission denied');
      }
    } else {
      this.app.logger.info('notification permission granted');
    }
  };

  /**
   * Registers a handler to reassert microphone access should device changes occur
   */
  private readonly registerMicrophoneChangeHandler = () => {
    /**
     * Clears out any currently registered handler
     **/
    const clearCurrentHandler = () => {
      const currentCleanupFn = this._microphoneChangeCleanupFn;
      this._microphoneChangeCleanupFn = undefined;
      currentCleanupFn?.();
    };

    clearCurrentHandler();
    const onDeviceChange = () => {
      clearTimeout(this._microphoneChangeTimeout);

      // initiate a debounced request to reassert microphone access
      // we debounce because a single microphone disconnect can fire multiple devicechange events
      this._microphoneChangeTimeout = setTimeout(() => {
        this._microphoneChangeTimeout = undefined;
        this.requestMicrophoneAccess();
      }, MICROPHONE_CHANGE_DEBOUNCE_MS);
    };

    // register the handler
    window.navigator.mediaDevices.addEventListener(
      'devicechange',
      onDeviceChange,
    );

    // register the cleanup function so we can handle multiple calls (edge case)
    this._microphoneChangeCleanupFn = () => {
      window.navigator.mediaDevices.removeEventListener(
        'devicechange',
        onDeviceChange,
      );
    };
  };

  public requestMicrophoneAccess = async () => {
    const hasGracefulMicrophoneDisconnect = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect,
    );

    try {
      const devices = await window.navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      if (devices) {
        browserState.microphoneAccess = MicrophoneAccessType.ACCESS_GRANTED;

        if (hasGracefulMicrophoneDisconnect) {
          // clean up any previous attempts
          browserState.tries = 0;
          browserState.showMicrophoneAccessModal = false;
          AnnouncementsModule.hideAnnouncement(
            MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
          );
        }
      }
    } catch (e: unknown) {
      const isPermissionError = (e as DOMException).name === 'NotAllowedError';
      if (isPermissionError) {
        browserState.microphoneAccess = MicrophoneAccessType.PERMISSION_DENIED;
      } else {
        browserState.microphoneAccess = MicrophoneAccessType.ERROR;
      }

      browserState.tries++;
      if (browserState.tries >= 2) {
        browserState.showMicrophoneAccessModal = true;
      }

      AnnouncementsModule.showAnnouncement(
        {
          title: strings.microphoneAccess.error,
          status: 'warning',
          onClose: () =>
            AnnouncementsModule.hideAnnouncement(
              MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
            ),
          ...(hasGracefulMicrophoneDisconnect && {
            title: strings.microphoneAccess.errorSoft,
          }),
          ...(isPermissionError && {
            title: strings.microphoneAccess.noPermission,
            actionName: strings.microphoneAccess.requestPermissionCta,
            onActionClick: (): void => {
              this.requestMicrophoneAccess();
            },
          }),
        },
        MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
      );
    }
  };

  /**
   * Requests a one-shot GPS position fix and updates `browserState.localeState`
   * with the result.
   *
   * Designed to be called in response to an explicit user action (the "I'm in a
   * supported region" button).  It may trigger the browser's geolocation
   * permission prompt if the user has not yet made a decision.
   *
   * On **success** (GPS returns coordinates): `localeState.locale` and
   * `localeState.source` are updated.  Geolocation is treated as authoritative
   * — `source` is set to `Geolocation` even when `locale` resolves to
   * `Unsupported`, hiding the escape-hatch button since GPS already confirmed
   * the user's position.  `startGeolocationWatch` is then called so subsequent
   * position changes continue to update state without another user action.
   *
   * On **failure** (API unavailable, permission denied, timeout, position
   * unavailable): `localeState.locale` and `localeState.source` are left
   * unchanged, and a {@link GeolocationDetectionError} is re-thrown so the
   * caller can present a targeted error message to the user.
   *
   * `localeState.isLoading` is set to `true` for the duration and reset to
   * `false` in a `finally` block, guaranteeing cleanup on both success and
   * failure paths.
   *
   * @throws {GeolocationDetectionError} When {@link detectLocaleFromGeolocation}
   *         fails to acquire a GPS fix.
   */
  public attemptGeolocationDetection = async (): Promise<void> => {
    browserState.localeState.isLoading = true;
    try {
      const locale = await detectLocaleFromGeolocation();
      browserState.localeState.locale = locale;
      browserState.localeState.source = LocaleSource.Geolocation;
    } finally {
      browserState.localeState.isLoading = false;
    }
    // Reaches here only on GPS success — permission was just granted, so
    // start watching to keep state current as the user moves.
    this.startGeolocationWatch();
  };

  /**
   * Clears browser data (localStorage, sessionStorage, IndexedDB, caches, cookies)
   * while preserving authentication-related data and tasks.
   *
   * Note: Auth is handled via sessionStorage (auth_response), localStorage (OAuth tokens),
   * and cookies on login domains (login.servicetitan.com, login-stage.servicetitan.com).
   * Cookies on login domains cannot be cleared from this app due to browser security restrictions.
   */
  public async clearBrowserData({
    keepTasks,
  }: {
    keepTasks: boolean;
  }): Promise<void> {
    this.app.logger.info('Clearing browser data while preserving auth');

    try {
      // 1. Preserve auth data from localStorage
      const localStorageDataToPreserve = {
        serviceId: localStorage.getItem(STORAGE_KEY_SERVICE_ID),
        serviceName: localStorage.getItem(STORAGE_KEY_SERVICE_NAME),
        refreshedAt: localStorage.getItem(STORAGE_KEY_REFRESHED_AT),
        tasks: localStorage.getItem(STORAGE_KEY_TASKS),
      };

      // 2. Preserve auth data from sessionStorage
      const sessionStorageDataToPreserve = {
        auth_response: sessionStorage.getItem(STORAGE_KEY_AUTH_RESPONSE),
        auth_redirect_url: sessionStorage.getItem(
          STORAGE_KEY_AUTH_REDIRECT_URL,
        ),
      };

      // 3. Preserve OAuth AppAuth data (keys starting with STORAGE_PREFIX_APPAUTH)
      const oauthKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX_APPAUTH)) {
          oauthKeys.push(key);
        }
      }
      const oauthData = oauthKeys.reduce(
        (acc, key) => {
          acc[key] = localStorage.getItem(key);
          return acc;
        },
        {} as Record<string, string | null>,
      );

      // 4. Clear localStorage
      localStorage.clear();

      // 5. Clear sessionStorage
      sessionStorage.clear();

      // 6. Restore auth data to localStorage
      if (localStorageDataToPreserve.serviceId) {
        localStorage.setItem(
          STORAGE_KEY_SERVICE_ID,
          localStorageDataToPreserve.serviceId,
        );
      }
      if (localStorageDataToPreserve.serviceName) {
        localStorage.setItem(
          STORAGE_KEY_SERVICE_NAME,
          localStorageDataToPreserve.serviceName,
        );
      }
      if (localStorageDataToPreserve.refreshedAt) {
        localStorage.setItem(
          STORAGE_KEY_REFRESHED_AT,
          localStorageDataToPreserve.refreshedAt,
        );
      }
      if (keepTasks && localStorageDataToPreserve.tasks) {
        localStorage.setItem(
          STORAGE_KEY_TASKS,
          localStorageDataToPreserve.tasks,
        );
      }

      // 7. Restore OAuth data
      Object.entries(oauthData).forEach(([key, value]) => {
        if (value) {
          localStorage.setItem(key, value);
        }
      });

      // 8. Restore auth data to sessionStorage
      if (sessionStorageDataToPreserve.auth_response) {
        sessionStorage.setItem(
          STORAGE_KEY_AUTH_RESPONSE,
          sessionStorageDataToPreserve.auth_response,
        );
      }
      if (sessionStorageDataToPreserve.auth_redirect_url) {
        sessionStorage.setItem(
          STORAGE_KEY_AUTH_REDIRECT_URL,
          sessionStorageDataToPreserve.auth_redirect_url,
        );
      }

      // 9. Clear IndexedDB (Apollo Client cache and others)
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        const deletionPromises = databases
          .filter((db) => db.name)
          .map(
            (db) =>
              new Promise<void>((resolve, reject) => {
                const request = window.indexedDB.deleteDatabase(db.name!);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
                request.onblocked = () => {
                  this.app.logger.warn(
                    `IndexedDB deletion blocked for ${db.name}`,
                  );
                  // Resolve anyway to not block the overall operation
                  resolve();
                };
              }),
          );
        await Promise.all(deletionPromises);
      }

      // 10. Clear Cache Storage (if Service Workers exist)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName)),
        );
      }

      // 11. Clear all cookies accessible from this domain
      const cookies = document.cookie.split(';');

      // Parent domains where cookies may be set:
      // - .st.dev: QA environment
      // - .servicetitan.com: Stage and Production environments
      const parentDomains = ['.st.dev', '.servicetitan.com'];

      for (const cookie of cookies) {
        const cookieName = cookie.split('=')[0].trim();
        // Clear for current path without domain (browser default)
        // This handles localhost and any other domain not in the parent domains list
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Clear for known parent domains (QA: .st.dev, Stage/Prod: .servicetitan.com)
        for (const domain of parentDomains) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        }
      }

      this.app.logger.info('Browser data cleared successfully');
    } catch (error) {
      this.app.logger.error('Error clearing browser data', error);
      const errorMessage = getClearBrowserDataErrorMessage(error);
      throw new Error(errorMessage);
    }
  }
}

export default new BrowserModule(
  appManager.events,
  appManager.logger('BrowserModule'),
);
